import { isNotNullish } from 'effect/Predicate'

import { getTailwindColorByIndex } from '../../../../constant/colors.ts'
import type { CoreModel } from '../../../../schemas/model/model.ts'
import type { ModelProperty } from '../../../../schemas/model/modelProperty.ts'
import type { NodePath, ViewQueryField, ViewQueryNode } from '../../../../schemas/query/index.ts'
import type { ViewUIChildRecordsForm, ViewUICol, ViewUIRow } from '../../../../schemas/ui/component.ts'
import type { DataEntryCommon, ViewUIDataEntry } from '../../../../schemas/ui/dataEntry.ts'
import type { ViewUIFormItem } from '../../../../schemas/ui/form.ts'
import type {
  NodePropertyName,
  ViewConfigForm,
  ViewConfigTreeNode,
  ViewConfigUiComponent,
} from '../../../../schemas/view_config/index.ts'
import { POSTGRES_COLUMN_LENGTH_LIMIT } from '../../../query/types.ts'
import { isRequiredProperty } from '../../../util/isRequiredProperty.ts'
import { makeIdentifiable } from '../../../util/makeIdentifiable.ts'
import type { CompileContext } from '../../common.ts'
import { generateSelectOptionColor } from '../sheet/compileSheetViewConfigFields.ts'

export interface RowFieldAndComponentNode {
  type: 'Row'
  configComponent: ViewConfigUiComponent
  component: ViewUIRow
  children: ColFieldAndComponentNode[]
}

export interface ColFieldAndComponentNode {
  type: 'Col'
  configComponent: ViewConfigUiComponent
  component: ViewUICol
  children: FieldAndComponentNode[]
}

export interface ChildRecordsFormFieldAndComponentNode {
  type: 'ChildRecordsForm'
  configComponent: ViewConfigUiComponent
  component: ViewUIChildRecordsForm
  queryFields: ViewQueryField[]
  children: FieldAndComponentNode[]
}

export interface FormItemFieldAndComponentNode {
  type: 'FormItem'
  configComponent: ViewConfigUiComponent
  component: ViewUIFormItem
  queryFields: ViewQueryField[]
}

export type FieldAndComponentNode =
  | RowFieldAndComponentNode
  | ColFieldAndComponentNode
  | ChildRecordsFormFieldAndComponentNode
  | FormItemFieldAndComponentNode

export function flatFieldAndComponentNodes(node: FieldAndComponentNode): FieldAndComponentNode[] {
  switch (node.type) {
    case 'FormItem': {
      return [node]
    }
    case 'Row':
    case 'Col':
    case 'ChildRecordsForm': {
      return [node, ...node.children.flatMap(flatFieldAndComponentNodes)]
    }
  }
}

export function getQueryFieldsOfFieldAndComponentNode(node: FieldAndComponentNode): ViewQueryField[] {
  switch (node.type) {
    case 'FormItem':
    case 'ChildRecordsForm': {
      return node.queryFields
    }
    case 'Row':
    case 'Col': {
      return []
    }
  }
}

interface FormContext {
  context: CompileContext
  queryNodes: ViewQueryNode[]
  configNodes: ViewConfigTreeNode[]
}

// 雑に色を決定
let colorCount = 0

export function compileFormViewConfigComponents(
  components: ViewConfigForm['components'],
  context: FormContext,
): FieldAndComponentNode[] {
  if (components === undefined) {
    return []
  }
  colorCount = 0

  return components.map((component) => compile(component, context)).filter(isNotNullish)
}

function compile(component: ViewConfigUiComponent, context: FormContext): FieldAndComponentNode | undefined {
  if (component.componentType === undefined && component.property === undefined) {
    return undefined
  }

  if (component.property !== undefined) {
    return compileAsPropertyComponent(component, component.property, context)
  }
  return compileAsUiComponent(component, context)
}

function compileAsUiComponent(
  configComponent: ViewConfigUiComponent,
  formContext: FormContext,
): FieldAndComponentNode | undefined {
  const children = (configComponent.children ?? []).map((child) => compile(child, formContext)).filter(isNotNullish)
  const type = configComponent.componentType
  switch (type) {
    case 'Row': {
      // Row.children は Col のみを許容するスキーマなので、Col 以外が混ざっていれば捨てる
      return {
        type: 'Row',
        configComponent,
        component: {
          type: 'Row',
          id: ``, // これ何に使うんだっけ？
          children: [], // childrenは後で合成
        },
        children: children.filter((c): c is ColFieldAndComponentNode => c.type === 'Col'),
      }
    }
    case 'Col': {
      return {
        type: 'Col',
        configComponent,
        component: {
          type: 'Col',
          id: ``, // これ何に使うんだっけ？
          span: 12,
          children: [],
        },
        children,
      }
    }
    case 'ChildRecordsForm': {
      const { queryNodes, configNodes, context } = formContext
      const queryNode = queryNodes.find((x) => x.name === configComponent.nodeName)
      const configNode = configNodes.find((x) => x.name === configComponent.nodeName)
      const additionalFields = getAdditionalQueryField(queryNode, configNode, context)
      const model = context.modelSearcher.searchModel(configNode?.modelName)
      return {
        type: 'ChildRecordsForm',
        configComponent,
        component: {
          type: 'ChildRecordsForm',
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion,sonarjs/todo-tag
          label: queryNode?.meta.label ?? configComponent.nodeName!, // TODO
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          tableNodeName: configComponent.nodeName!,
          nameFieldName: additionalFields?.nameQueryField?.name,
          recordUrlFieldName: additionalFields?.recordUrlQueryField?.name,
          children: [],
          // relation: configNode?.referenceToProperty?.modelName === configNode?.modelName ? 'one_to_many' : 'many_to_one'
          relation: queryNode?.read.join?.relation,
          // eslint-disable-next-line sonarjs/no-nested-assignment
          color: model?.color ?? getTailwindColorByIndex((colorCount += 1)),
          icon: model?.icon,
        },
        queryFields: [additionalFields?.nameQueryField, additionalFields?.recordUrlQueryField].filter(isNotNullish),
        children,
      }
    }
    default: {
      break
    }
  }
  throw new Error(`[compileAsUiComponent] not implemented. ${type ?? ''}`)
}

export function getAdditionalQueryField(
  queryNode: ViewQueryNode | undefined,
  configNode: ViewConfigTreeNode | undefined,
  context: CompileContext,
) {
  if (queryNode === undefined || configNode === undefined) {
    return
  }
  const model = context.modelSearcher.searchModel(configNode.modelName)
  if (model === undefined) {
    return
  }
  const nameProperty = model.properties.find((x) => x.meta === 'name')
  const recordUrlProperty = model.properties.find((x) => x.meta === 'record_url')
  const nameQueryField = getFieldFromProperty(nameProperty, model, queryNode, configNode, context)
  const recordUrlQueryField = getFieldFromProperty(recordUrlProperty, model, queryNode, configNode, context)
  return {
    nameQueryField,
    recordUrlQueryField,
  }
}

// eslint-disable-next-line @typescript-eslint/max-params
function getFieldFromProperty(
  property: ModelProperty | undefined,
  model: CoreModel,
  queryNode: ViewQueryNode,
  configNode: ViewConfigTreeNode,
  context: CompileContext,
) {
  if (property === undefined) {
    return
  }

  const name = makeIdentifiable([queryNode.name, property.name].join('_'), POSTGRES_COLUMN_LENGTH_LIMIT)
  const field: ViewQueryField = {
    name,
    nodePath: queryNode.path,
    read: {
      sql: `"${queryNode.name}"."${property.name}"`,
      ...getReferenceRelatedSql(configNode, queryNode, property, model.name, context),
      // ...component.override?.queryField?.read, // fieldのoverride
    },
    write:
      property.write === undefined ||
      property.write === null ||
      (property.creatable === false && property.updatable === false)
        ? undefined
        : {
            streamName: configNode.modelName,
            propertySourceName: property.write.sourcePropertyName,
          },
    meta: {
      label: property.label,
      fieldType: property.type,
      fieldMetaType: property.meta ?? undefined,
      required: isRequiredProperty(property),
      defaultValue: undefined,
      searchable: property.meta === 'name',
      dependedPropertyNamesWithStreamName: [],
      // ...component.override?.queryField?.meta,
      // 以下、単純にoverrideさせない項目
      creatable:
        // (component.override?.queryField?.meta?.creatable ?? true) &&
        (property.creatable ?? true) && isNotNullish(property.write),
      updatable:
        // (component.override?.queryField?.meta?.updatable ?? true) &&
        (property.updatable ?? true) && isNotNullish(property.write),
    },
  }
  return field
}

function compileAsPropertyComponent(
  component: ViewConfigUiComponent,
  nodeProperty: NodePropertyName,
  formContext: FormContext,
): FieldAndComponentNode | undefined {
  const { queryNodes, configNodes, context } = formContext
  const queryNode = queryNodes.find((node) => node.name === nodeProperty.nodeName)
  const configNode = configNodes.find((node) => node.name === nodeProperty.nodeName)
  const model = context.modelSearcher.searchModel(nodeProperty.modelName)
  const property = context.modelSearcher.searchProperty(nodeProperty.modelName, nodeProperty.propertyName)

  if (queryNode === undefined || configNode === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    return undefined // TODO
  }
  if (property === undefined || model === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    return undefined // TODO
  }

  const name = makeIdentifiable([queryNode.name, property.name].join('_'), POSTGRES_COLUMN_LENGTH_LIMIT)
  const queryField: ViewQueryField = {
    name,
    nodePath: queryNode.path,
    read: {
      // eslint-disable-next-line sonarjs/todo-tag
      sql: `"${queryNode.name}"."${property.name}"`, // TODO: このレイヤーでSQL変換したくない
      ...getReferenceRelatedSql(configNode, queryNode, property, model.name, formContext.context),
      // ...component.override?.queryField?.read, // fieldのoverride
    },
    write:
      property.write === undefined ||
      property.write === null ||
      (property.creatable === false && property.updatable === false)
        ? undefined
        : {
            streamName: nodeProperty.modelName,
            propertySourceName: property.write.sourcePropertyName,
          },
    meta: {
      label: property.label,
      fieldType: property.type,
      fieldMetaType: property.meta ?? undefined,
      required: isRequiredProperty(property),
      defaultValue: undefined,
      searchable: property.meta === 'name',
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO
      // conditionalEffects: isSome(component.conditionalEffects)
      //   ? component.override!.conditionalEffects.map((x) => compileConditionalEffects(x.expression, x.effect)).compact()
      //   : undefined,
      dependedPropertyNamesWithStreamName: [
        {
          streamName: nodeProperty.modelName,
          propertyName: nodeProperty.propertyName,
        },
      ],
      ...component.override?.queryField?.meta,
      // 以下、単純にoverrideさせない項目
      creatable:
        // (component.override?.queryField?.meta?.creatable ?? true) &&
        (property.creatable ?? true) && isNotNullish(property.write),
      updatable:
        // (component.override?.queryField?.meta?.updatable ?? true) &&
        (property.updatable ?? true) && isNotNullish(property.write),
    },
  }

  const common: DataEntryCommon = {
    fieldName: queryField.name,
    readonly: queryField.write === undefined,
    required: isRequiredProperty(property),
  }
  return {
    type: 'FormItem',
    configComponent: component,
    queryFields: [queryField],
    component: {
      ...common,
      type: 'FormItem',
      // eslint-disable-next-line sonarjs/todo-tag
      label: property.label, // TODO: override
      child: getDataEntryFromField(queryField, property, common),
    },
  }
}

// eslint-disable-next-line @typescript-eslint/max-params
function getReferenceRelatedSql(
  configNode: ViewConfigTreeNode,
  queryNode: ViewQueryNode,
  property: ModelProperty,
  propertyModelName: string,
  context: CompileContext,
): { labelSql: string; searchSql: string; labelNodePath: NodePath } | undefined {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: polymorphicのときにどうすべきか、現状解がない

  const xs = (property.referenceTo ?? []).map((reference) => {
    const childConfigNode = (configNode.children ?? []).find(
      (childNode) =>
        childNode.referenceToProperty?.propertyName === property.name &&
        childNode.referenceToProperty.modelName === propertyModelName &&
        childNode.referenceToProperty.referenceTo.modelName === reference.modelName,
    )
    const childQueryNode = (queryNode.children ?? []).find((x) => x.name === childConfigNode?.name)
    if (childQueryNode === undefined || childConfigNode === undefined) {
      // eslint-disable-next-line array-callback-return
      return
    }

    const childModel = context.modelSearcher.searchModel(childConfigNode.modelName)
    const nameProperty = childModel?.properties.find((x) => x.meta === 'name')
    return {
      // eslint-disable-next-line sonarjs/todo-tag
      labelSql: `"${childQueryNode.name}"."${nameProperty?.name ?? 'id'}"`, // TODO: ここでSQLを組み立てない
      labelNodePath: childQueryNode.path,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      searchSql: childQueryNode.read.join!.search.sql,
    }
  })
  return xs.find(isNotNullish)
}

function getDataEntryFromField(
  field: ViewQueryField,
  property: ModelProperty,
  common: DataEntryCommon,
): ViewUIDataEntry {
  if (field.read.searchSql !== undefined && field.read.labelSql !== undefined) {
    return {
      type: 'SearchSelect',
      fieldName: field.name,
      labelFieldName: `${field.name}_label`,
      searchSql: field.read.searchSql,
    }
  }
  if ((property.selectOptions ?? []).length > 0) {
    return {
      ...common,
      type: 'Select',
      options: (property.selectOptions ?? []).map((x, index) => ({
        ...x,
        color: x.color ?? generateSelectOptionColor(index),
      })),
      multiple: property.meta === 'multi_select',
    }
  }
  switch (field.meta.fieldMetaType) {
    case 'text': {
      return {
        type: 'Input',
        textarea: true,
        ...common,
      }
    }
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    case 'html': {
      return {
        type: 'Input',
        textarea: true,
        ...common,
      }
    }
    default: {
      break
    }
  }
  switch (field.meta.fieldType) {
    case 'string': {
      return {
        type: 'Input',
        ...common,
      }
    }
    case 'numeric': {
      return {
        type: 'InputNumber',
        ...common,
      }
    }
    case 'boolean': {
      return {
        type: 'Checkbox',
        ...common,
      }
    }
    case 'date': {
      return {
        type: 'DatePicker',
        ...common,
      }
    }
    case 'datetime': {
      return {
        type: 'DatePicker',
        showTime: true,
        // minuteStep: 5,
        ...common,
      }
    }
    default: {
      break
    }
  }
  return {
    type: 'Input',
    ...common,
  }
}
